import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';

/**
 * Add a property to the selected properties.
 * @param {string} propertyKey
 * @param {number|undefined} index
 * @returns {Function}
 */
const addSelectedProperty = (propertyKey, index = undefined) => async ({
  properties,
  customProperties,
  setSelectedProperties,
  selectedProperties,
  onSelectProperty,
}) => {
  try {
    let property = findObjectInArray(properties, 'key', propertyKey);

    if (!property) {
      property = findObjectInArray(customProperties, 'key', propertyKey);
    }

    if (!property) {
      return;
    }

    const selectedProperty = findObjectInArray(selectedProperties, 'key', propertyKey);

    if (selectedProperty) {
      return;
    }

    const newSelectedProperties = [...selectedProperties];
    const isValidIndex = index !== undefined && index >= 0 && index < selectedProperties.length;
    const position = isValidIndex ? index : selectedProperties.length;

    newSelectedProperties.splice(position, 0, property);

    if (onSelectProperty) {
      await onSelectProperty(property, newSelectedProperties);
    }

    setSelectedProperties(newSelectedProperties);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Remove a property from the selected properties.
 * @param {string} propertyKey
 * @returns {Function}
 */
const removeSelectedProperty = propertyKey => async ({
  selectedProperties,
  setSelectedProperties,
  onRemoveSelectedProperty,
}) => {
  try {
    const property = findObjectInArray(selectedProperties, 'key', propertyKey);

    if (!property) {
      return;
    }

    const newSelectedProperties = selectedProperties.filter(
      selectedProperty => selectedProperty.key !== propertyKey,
    );

    if (onRemoveSelectedProperty) {
      await onRemoveSelectedProperty(property, newSelectedProperties);
    }

    setSelectedProperties(newSelectedProperties);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Add a custom property.
 * @param {object} property
 * @returns {Function}
 */
const addCustomProperty = property => async ({
  customProperties,
  setCustomProperties,
  onAddCustomProperty,
}) => {
  try {
    const newCustomProperties = [...customProperties, property];

    if (onAddCustomProperty) {
      await onAddCustomProperty(property, newCustomProperties);
    }

    setCustomProperties(newCustomProperties);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Remove a custom property.
 * @param {string} propertyKey
 * @returns {Function}
 */
const removeCustomProperty = propertyKey => async state => {
  const {
    customProperties,
    setCustomProperties,
    selectedProperties,
    onRemoveCustomProperty,
  } = state;

  try {
    const property = findObjectInArray(customProperties, 'key', propertyKey);

    if (!property) {
      return;
    }

    const isSelected = !!findObjectInArray(selectedProperties, 'key', propertyKey);

    if (isSelected) {
      await removeSelectedProperty(propertyKey)(state);
    }

    const newCustomProperties = customProperties.filter(
      customProperty => customProperty.key !== propertyKey,
    );

    if (onRemoveCustomProperty) {
      await onRemoveCustomProperty(property, newCustomProperties);
    }

    setCustomProperties(newCustomProperties);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Add a fixed property.
 * @param {object} property
 * @returns {Function}
 */
const addFixedProperty = property => async ({
  fixedProperties,
  setFixedProperties,
  onAddFixedProperty,
}) => {
  try {
    const newFixedProperties = [...fixedProperties, property];

    if (onAddFixedProperty) {
      await onAddFixedProperty(property, newFixedProperties);
    }

    setFixedProperties(newFixedProperties);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Remove a fixed property.
 * @param {string} propertyKey
 * @returns {Function}
 */
const removeFixedProperty = propertyKey => async ({
  fixedProperties,
  setFixedProperties,
  onRemoveFixedProperty,
}) => {
  try {
    const property = findObjectInArray(fixedProperties, 'key', propertyKey);

    if (!property) {
      return;
    }

    const newFixedProperties = fixedProperties.filter(
      fixedProperty => fixedProperty.key !== propertyKey,
    );

    if (onRemoveFixedProperty) {
      await onRemoveFixedProperty(property, newFixedProperties);
    }

    setFixedProperties(newFixedProperties);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Clear all selected properties.
 * @returns {Function}
 */
const clearSelectedProperties = () => async ({
  setSelectedProperties,
  onClearSelectedProperties,
}) => {
  try {
    if (onClearSelectedProperties) {
      await onClearSelectedProperties();
    }

    setSelectedProperties([]);
  } catch (err) {
    // Do something with error.
  }
};

/**
 * Return the available (non-selected) properties.
 * @returns {array}
 */
const getAvailableProperties = () => ({
  selectedProperties,
  properties,
  customProperties,
  sortDirection,
  sortKey,
  searchValue,
}) => {
  const availableProperties = properties.filter(
    property => !findObjectInArray(selectedProperties, 'key', property.key),
  );
  const availableCustomProperties = customProperties.filter(
    customProperty => !findObjectInArray(selectedProperties, 'key', customProperty.key),
  );

  const sortedAvailableProperties = sortArrayByProperty(
    [...availableProperties, ...availableCustomProperties],
    sortKey,
    sortDirection,
  );

  return sortedAvailableProperties.filter(
    availableProperty =>
      availableProperty[sortKey].toLowerCase().indexOf(searchValue?.toLowerCase()) > -1,
  );
};

/**
 * Return all active properties.
 * @returns {array}
 */
const getActiveProperties = () => ({ fixedProperties, selectedProperties }) => {
  const formattedFixedProperties = fixedProperties.reduce(
    (acc, fixedProperty) => [...acc, { isSortable: false, ...fixedProperty }],
    [],
  );

  return [...formattedFixedProperties, ...selectedProperties];
};

export default {
  addSelectedProperty,
  removeSelectedProperty,
  addCustomProperty,
  removeCustomProperty,
  addFixedProperty,
  removeFixedProperty,
  clearSelectedProperties,
  getAvailableProperties,
  getActiveProperties,
};
